import React, { useState, useRef, useCallback } from 'react';
import ReactCrop from 'react-easy-crop';
import rotationImg from '../../assets/images/rotation.svg';

interface CropImageProps {
  image: string | undefined;
  cropAsp?: any;
  onChange: (file: any, isUpload?: boolean) => void;
  handleModal: () => void;
}

const CropImage: React.FC<CropImageProps> = ({
  image,
  cropAsp,
  onChange,
  handleModal,
}) => {
  const [crop, setCrop] = useState<any>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [rotation, setRotation] = useState<number>(0);
  const [cropPreview, setCropPreview] = useState(null);

  const onCropChange = useCallback(
    (newCrop: React.SetStateAction<{ x: number; y: number }>) => {
      setCrop(newCrop);
    },
    []
  );

  const onCropComplete = useCallback(
    (croppedArea: any, croppedAreaPixels: any) => {
      setCropPreview(croppedAreaPixels);
    },
    []
  );

  const onZoomChange = useCallback((newZoom: React.SetStateAction<number>) => {
    setZoom(newZoom);
  }, []);
  const fileNull = null;
  const handleCropCancel = useCallback(() => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCropPreview(null);
    handleModal();
    onChange(fileNull, true);
  }, [onChange]);

  const createImage = (url: any) =>
    new Promise((resolve, reject) => {
      const image = new Image();
      image.addEventListener('load', () => resolve(image));
      image.addEventListener('error', (error) => reject(error));
      image.setAttribute('crossOrigin', 'anonymous');
      image.src = url;
    });

  const getRadianAngle = (degreeValue: number) => {
    return (degreeValue * Math.PI) / 180;
  };

  const rotateSize = (width: number, height: number, rotation: number) => {
    const rotRad = getRadianAngle(rotation);

    return {
      width:
        Math.abs(Math.cos(rotRad) * width) +
        Math.abs(Math.sin(rotRad) * height),
      height:
        Math.abs(Math.sin(rotRad) * width) +
        Math.abs(Math.cos(rotRad) * height),
    };
  };

  const getCroppedImg = async (image: any, crop: any, rotation: any = 0) => {
    const img: any = await createImage(image);
    const canvas: any = document.createElement('canvas');
    const ctx: any = canvas.getContext('2d');

    if (!ctx) {
      return null;
    }

    const rotRad = getRadianAngle(rotation);

    const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
      img.width,
      img.height,
      rotation
    );

    canvas.width = bBoxWidth;
    canvas.height = bBoxHeight;

    ctx.fillStyle = '#FFFFFF'; // Set background color to white
    ctx.fillRect(0, 0, bBoxWidth, bBoxHeight); // Fill the canvas with white

    ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
    ctx.rotate(rotRad);
    ctx.translate(-img.width / 2, -img.height / 2);

    ctx.drawImage(img, 0, 0);

    const croppedCanvas = document.createElement('canvas');

    const croppedCtx = croppedCanvas.getContext('2d');

    if (!croppedCtx) {
      return null;
    }

    croppedCanvas.width = crop.width;
    croppedCanvas.height = crop.height;

    croppedCtx.drawImage(
      canvas,
      crop.x,
      crop.y,
      crop.width,
      crop.height,
      0,
      0,
      crop.width,
      crop.height
    );
    const convertImage: any = (quality: number) => {
      const base64 = croppedCanvas.toDataURL('image/jpeg', quality);
      const imgSize = atob(base64.split(',')[1]).length / 1024 / 1024;
      if (Math.round(imgSize) > 2) {
        return convertImage(quality - 0.05);
      } else {
        return base64;
      }
    };
    return convertImage(1);
  };

  const rotateClockwise = () => {
    setRotation(rotation + 45);
  };

  const rotateAntiClockwise = () => {
    setRotation(rotation - 45);
  };
  const handleCropSave = useCallback(
    async (event: any) => {
      if (cropPreview) {
        try {
          const croppedImageBlob: any = await getCroppedImg(
            image,
            cropPreview,
            rotation
          );
          onChange(croppedImageBlob);
          setCropPreview(null);
        } catch (error) {
          console.error('Error saving cropped image:', error);
        }
      }
    },
    [cropPreview, image, onChange]
  );
  return (
    <div className="flex flex-col gap-4">
      <div className="relative h-[255px] max-w-[340px] ">
        <ReactCrop
          image={image}
          aspect={cropAsp ? cropAsp : 1 / 1}
          crop={crop}
          zoom={zoom}
          rotation={rotation}
          onCropChange={onCropChange}
          onCropComplete={onCropComplete}
          onZoomChange={onZoomChange}
        />
      </div>
      <div className="flex justify-between">
        <img
          src={rotationImg}
          alt={rotationImg}
          onClick={rotateAntiClockwise}
        />
        <img
          src={rotationImg}
          alt={rotationImg}
          className="scale-x-[-1]"
          onClick={rotateClockwise}
        />
      </div>
      <div className="">
        <input
          type="range"
          min="1"
          step="0.01"
          aria-labelledby="Zoom"
          onInput={(event: React.ChangeEvent<HTMLInputElement>) => {
            setZoom(parseFloat(event.target.value));
          }}
          value={zoom}
          max="3"
          className="w-full   cursor-pointer h-1 bg-light-grey rounded"
        />
      </div>

      <div className="flex justify-end gap-6">
        <button
          type="button"
          onClick={handleCropCancel}
          className="text-secondary-0 text-base"
        >
          Cancel
        </button>
        <button
          // disabled={true}
          type="button"
          onClick={handleCropSave}
          className="text-pure-white text-base rounded-lg px-[52px] py-[10px] bg-primary"
        >
          Upload
        </button>
      </div>
    </div>
  );
};

export default React.memo(CropImage);
